import { useRef, useState, useEffect, useMemo } from 'react';
import {
  Button,
  Card,
  Box,
  CardContent,
  CardHeader,
  Divider,
  styled,
  useTheme
} from '@mui/material';
import { useDataProvider } from 'react-admin';
import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { useCurrentProject } from '../../contexts/AccountProjectContext';
import { useCanAccess } from '@react-admin/ra-rbac';
import { brand } from '../../themes/brandTokens';

const euroFormat = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const compactFormat = new Intl.NumberFormat('fr-FR', { notation: 'compact', maximumFractionDigits: 1 });
const euro = (val: number) => euroFormat.format(val);
const euroCompact = (val: number) => compactFormat.format(val) + ' €';

const DotPrimaryLight = styled('span')(({ theme }) => `
  border-radius: 22px;
  background: ${theme.colors.primary.lighter};
  width: ${theme.spacing(1.5)};
  height: ${theme.spacing(1.5)};
  display: inline-block;
  margin-right: ${theme.spacing(0.5)};
`);

const DotPrimary = styled('span')(({ theme }) => `
  border-radius: 22px;
  background: ${theme.colors.primary.main};
  width: ${theme.spacing(1.5)};
  height: ${theme.spacing(1.5)};
  display: inline-block;
  margin-right: ${theme.spacing(0.5)};
`);

function TransactionsStatistics() {
  const { t }: { t: any } = useTranslation();
  const theme = useTheme();
  const dataProvider = useDataProvider();
  const { codeProject } = useCurrentProject();
  const { accountId } = useCurrentProject();
  const [statistics, setStatistics] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { isLoading: isLoadingStatistics, canAccess } = useCanAccess({
    action: 'list',
    resource: 'cryptoOrdersStatistics',
  });

  const [amount, setAmount] = useState(0);

  // Generate last 12 months
  const last12Months = useMemo(() => {
    const months = [];
    const currentDate = new Date();

    for (let i = 11; i >= 0; i--) {
      // Day 1 of each month keeps the month arithmetic from overflowing.
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      months.push({
        key: `${year}-${month}`,
        label: date.toLocaleString('en', { month: 'short' })
      });
    }
    return months;
  }, []);

  const [chartData, setChartData] = useState([
    {
      name: 'Transactions',
      data: Array(12).fill(0)
    }
  ]);

  // Fetch statistics when codeProject changes
  useEffect(() => {
    if (!accountId || isLoadingStatistics || !canAccess) return;
    const filter: any = {
      accountId,
      statusOrder: "success",
    };
    if (codeProject) {
      filter.codeProject = codeProject;
    }
    setIsLoading(true);
    dataProvider
      .getList('cryptoOrdersStatistics', {
        filter,
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'createdAt', order: 'DESC' }
      })
      .then(({ data }) => setStatistics(data))
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, [accountId, codeProject, dataProvider, isLoadingStatistics, canAccess]);

  // Update chart data
  useEffect(() => {
    const monthlyData = Object.fromEntries(last12Months.map(m => [m.key, 0]));
    statistics.forEach(stat => {
      if (monthlyData.hasOwnProperty(stat.month)) {
        monthlyData[stat.month] = stat.totalEuroAmount;
      }
    });

    const preparedData = last12Months.map(month => monthlyData[month.key]);
    setChartData([{ name: 'Transaction', data: preparedData }]);
  }, [statistics, last12Months]);

  // Bars carry the brand gradient (purple at the base, pink at the top); axes stay quiet.
  const chartOptions: ApexOptions = {
    chart: {
      background: 'transparent',
      type: 'bar',
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: theme.typography.fontFamily as string,
      foreColor: theme.palette.text.secondary
    },
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 6,
        borderRadiusApplication: 'end',
        columnWidth: '38%'
      }
    },
    colors: [brand.purple],
    fill: {
      type: 'gradient',
      gradient: {
        type: 'vertical',
        shadeIntensity: 0,
        gradientToColors: [brand.pink],
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 1,
        stops: [0, 100]
      }
    },
    states: {
      hover: { filter: { type: 'darken' } },
      active: { filter: { type: 'none' } }
    },
    dataLabels: { enabled: false },
    theme: { mode: theme.palette.mode },
    stroke: { show: false },
    legend: { show: false },
    xaxis: {
      labels: { style: { colors: theme.palette.text.secondary, fontSize: '12px' } },
      axisBorder: { show: false },
      axisTicks: { show: false },
      crosshairs: { fill: { color: theme.palette.background.default } },
      categories: last12Months.map(month => month.label)
    },
    yaxis: {
      labels: {
        style: { colors: theme.palette.text.secondary, fontSize: '12px' },
        formatter: (val) => euroCompact(val)
      }
    },
    tooltip: {
      theme: theme.palette.mode,
      y: { formatter: (val) => euro(val) },
      marker: { show: false }
    },
    grid: {
      strokeDashArray: 4,
      borderColor: theme.palette.divider,
      padding: { left: 8, right: 8 }
    }
  };

  return (
    <Card>
      <CardHeader
        title={t('Transaction volume')}
        subheader={t('Successful transactions per month, in euros, over the last 12 months.')}
      />
      <Divider />
      <CardContent>
        <Box
          sx={{
            pt: 3,
            px: { lg: 2 }
          }}
        >
          <Chart
            options={chartOptions}
            series={chartData}
            type="bar"
            height={288}
          />
        </Box>
      </CardContent>
    </Card>
  );
}

export default TransactionsStatistics;
